import {
  type FormValidateAsyncFn,
  createFormHook,
  createFormHookContexts,
  formOptions,
  useStore,
} from '@tanstack/react-form';
import { APIProvider, Map } from '@vis.gl/react-google-maps';
import {
  addDays,
  formatDistanceStrict,
  formatRelative,
  isBefore,
  startOfToday,
  startOfTomorrow,
} from 'date-fns';
import { ar } from 'date-fns/locale';
import {
  Ambulance,
  CalendarIcon,
  Info,
  Package,
  Plus,
  Trash2,
} from 'lucide-react';
import * as React from 'react';
import type { DateRange } from 'react-day-picker';
import { arSA, enUS } from 'react-day-picker/locale';
import { useTranslation } from 'react-i18next';
import { redirect, useLoaderData, useNavigate } from 'react-router';
import { toast } from 'sonner';
import { z } from 'zod';

import { APIError } from '@cold-ambulance/client';
import {
  AMBULANCE_SERVICE_TYPES,
  MIN_FIRST_PAYMENT_PERCENT,
  SHORT_TERM_SPLIT_OPTIONS,
  calculateMonthlyInstallments,
  isShortTermProject,
  toArabicOrdinal,
  toScheduleTime,
} from '@cold-ambulance/isomorphic';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Calendar,
  Input,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Slider,
  Textarea,
  ToggleGroup,
  ToggleGroupItem,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
} from '@cold-ambulance/shadcn';
import {
  CounterField,
  type EquipmentItem,
  EquipmentListField,
  ExpandableCheckbox,
  FieldMessage,
  FormMessage,
  TheButton,
  client,
  errorToFormIssue,
} from '@cold-ambulance/ui';

import i18n from '../../../i18n/i18n.ts';
import { requiresAuth } from '../../auth-loader.ts';
import { HoursField } from '../../components/hours-field.tsx';
import {
  type PreviewSectionId,
  RequestPreview,
} from '../../components/request-preview.tsx';
import { SearchPlaces } from '../../components/search-places.tsx';
import { YesNoField } from '../../components/yesno-field.tsx';
import {
  type DraftSaveStatus,
  clearLocalDraft,
  getLocalDraft,
  useRequestDraftAutosave,
} from '../../hooks/use-request-draft.ts';

function Section({ title }: { title: string }) {
  return (
    <h2 className="bg-primary/20 my-4 rounded-lg p-3 font-bold">{title}</h2>
  );
}

export const { fieldContext, formContext, useFieldContext, useFormContext } =
  createFormHookContexts();

export function TextField({ label }: { label: string }) {
  const field = useFieldContext<string>();

  return (
    <div className="grid w-full items-center gap-2">
      <Label className="text-sm" htmlFor="projectName">
        {label}
      </Label>
      <Input
        id="projectName"
        type="text"
        value={field.state.value}
        onBlur={field.handleBlur}
        onChange={(e) => field.handleChange(e.target.value)}
      />
      <FieldMessage errors={field.state.meta.errors} />
    </div>
  );
}

function SubscribeButton({ label }: { label: string }) {
  const form = useFormContext();
  return (
    <form.Subscribe
      selector={(state) => [state.canSubmit, state.isSubmitting]}
      children={([canSubmit, isSubmitting]) => (
        <TheButton
          type="submit"
          disabled={!canSubmit}
          loading={isSubmitting}
          className="mt-8 w-full cursor-pointer"
        >
          {label}
        </TheButton>
      )}
    />
  );
}

function ErrorMessage() {
  const form = useFormContext();
  return (
    <form.Subscribe
      selector={(state) => state.errors}
      children={(errors) => <FormMessage errors={errors} />}
    />
  );
}

const { useAppForm, withForm } = createFormHook({
  fieldComponents: {
    TextField,
  },
  formComponents: {
    SubscribeButton,
    ErrorMessage,
  },
  fieldContext,
  formContext,
});

export type RequestType = 'SERVICE' | 'EQUIPMENT';

const REQUEST_TYPE_OPTIONS = [
  {
    value: 'SERVICE',
    Icon: Ambulance,
    title: 'newRequest.serviceRequest',
    subtitle: 'newRequest.serviceRequestSubtitle',
  },
  {
    value: 'EQUIPMENT',
    Icon: Package,
    title: 'newRequest.equipmentRequest',
    subtitle: 'newRequest.equipmentRequestSubtitle',
  },
] as const satisfies readonly { value: RequestType; [k: string]: unknown }[];

function RequestTypeField({
  value,
  onChange,
}: {
  value: RequestType;
  onChange: (next: RequestType) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="mb-8">
      <ToggleGroup
        type="single"
        dir="rtl"
        className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2"
        value={value}
        onValueChange={(next) => {
          if (next) {
            onChange(next as RequestType);
          }
        }}
      >
        {REQUEST_TYPE_OPTIONS.map(
          ({ value: option, Icon, title, subtitle }) => (
            <ToggleGroupItem
              key={option}
              value={option}
              className="data-[state=on]:border-primary data-[state=on]:bg-primary/10 !h-auto flex-col items-start gap-1 !rounded-lg border p-4 whitespace-normal transition-transform duration-150 active:scale-[0.99]"
            >
              <span className="flex items-center gap-2 font-bold">
                <Icon className="size-4 shrink-0" />
                {t(title)}
              </span>
              <span className="text-muted-foreground text-xs font-normal">
                {t(subtitle)}
              </span>
            </ToggleGroupItem>
          ),
        )}
      </ToggleGroup>
    </div>
  );
}

function RequestSectionGroup({
  enabled,
  children,
}: React.PropsWithChildren<{ enabled: boolean }>) {
  return (
    <fieldset
      disabled={!enabled}
      className={cn(
        'min-w-0 transition-opacity duration-200',
        !enabled && 'pointer-events-none opacity-50',
      )}
    >
      {children}
    </fieldset>
  );
}

const formDefaults = {
  requestType: 'SERVICE' as RequestType,
  projectName: '',
  projectType: 'GOVERNMENT' as 'GOVERNMENT' | 'PRIVATE',
  location: { name: '', latitude: 0, longitude: 0 },
  dates: { from: '', to: '' },
  hours: { from: '', to: '' },
  notes: '',
  mealsProvided: false,
  housingTransportProvided: false,
  isolatedWorkArea: false,
  ambulanceServiceWithDriver: [
    {
      type1Amount: 0,
      type2Amount: 0,
      type3Amount: 0,
      type1Equipment: [] as EquipmentItem[],
      type2Equipment: [] as EquipmentItem[],
      type3Equipment: [] as EquipmentItem[],
    },
  ],
  ambulanceServiceWithoutDriver: [
    {
      type1Amount: 0,
      type2Amount: 0,
      type3Amount: 0,
      type1Equipment: [] as EquipmentItem[],
      type2Equipment: [] as EquipmentItem[],
      type3Equipment: [] as EquipmentItem[],
    },
  ],
  ambulance4x4WithDriver: { amount: 0, equipment: [] as EquipmentItem[] },
  ambulance4x4WithoutDriver: { amount: 0, equipment: [] as EquipmentItem[] },
  mobileClinicBasic: { amount: 0, equipment: [] as EquipmentItem[] },
  mobileClinicAdvanced: { amount: 0, equipment: [] as EquipmentItem[] },
  paramedicServices: [
    { amount: 0, type: 'SPECIALIST' },
    { amount: 0, type: 'TECHNICIAN' },
  ],
  nurseServices: [
    { amount: 0, type: 'SPECIALIST' },
    { amount: 0, type: 'TECHNICIAN' },
  ],
  doctorServices: [] as { specialization: string; amount: number }[],
  medicalEquipment: [] as { name: string; quantity: number }[],
  proposalDeadline: '',
  bankGuaranteePercent: 0,
  kpis: [] as string[],
  paymentSchedule: {
    paymentType: 'MONTHLY' as 'MONTHLY' | 'DELIVERABLES',
    milestones: [{ percentage: MIN_FIRST_PAYMENT_PERCENT }] as {
      name?: string;
      percentage: number;
    }[],
  },
};

const formOpts = formOptions({
  validators: {
    onSubmitAsync: async (
      _props: Parameters<FormValidateAsyncFn<typeof formDefaults>>[0],
    ): Promise<ReturnType<typeof errorToFormIssue> | undefined> => undefined,
    onSubmit: z
      .object({
        hours: z
          .object({
            from: z.string().min(1, { message: 'وقت البدء مطلوب' }),
            to: z.string().min(1, { message: 'وقت الانتهاء مطلوب' }),
          })
          .superRefine(({ from, to }, ctx) => {
            if (!from || !to) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'وقت البدء ووقت الانتهاء مطلوبان',
              });
              return;
            }
            if (isBefore(new Date(to), new Date(from))) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'وقت الانتهاء يجب أن يكون بعد وقت البدء',
              });
            }
          }),
        dates: z
          .object({
            from: z.string().min(1, { message: 'وقت البدء مطلوب' }),
            to: z.string().min(1, { message: 'وقت الانتهاء مطلوب' }),
          })
          .superRefine(({ from, to }, ctx) => {
            const fromDate = new Date(from);
            const toDate = new Date(to);
            if (!from || !to) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'تاريخ البدء و تاريخ الانتهاء مطلوبان',
              });
              return;
            }
            if (isBefore(toDate, fromDate)) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'تاريخ الانتهاء يجب أن يكون بعد تاريخ البدء',
              });
            }
          }),

        mealsProvided: z.boolean(),
        housingTransportProvided: z.boolean(),
        isolatedWorkArea: z.boolean(),
        notes: z.string(),
        projectName: z.string().min(1, { message: 'اسم جهة الاتصال مطلوب' }),
        requestType: z.enum(['SERVICE', 'EQUIPMENT'], {
          message: 'يجب اختيار نوع الطلب',
        }),
        projectType: z.enum(['GOVERNMENT', 'PRIVATE']),
        nurseServices: z.array(
          z.object({
            amount: z.number().int(),
            type: z.enum(['SPECIALIST', 'TECHNICIAN']),
          }),
        ),
        location: z
          .object({
            name: z.string().min(1, { message: 'اسم الموقع مطلوب' }),
            latitude: z.number(),
            longitude: z.number(),
          })
          .refine((value) => value.latitude !== 0 && value.longitude !== 0, {
            message: 'يرجى اختيار موقع صالح من القائمة',
          }),
        mobileClinicBasic: z.object({
          amount: z.number().int().min(0),
          equipment: z.array(
            z.object({ name: z.string(), quantity: z.number().int().min(1) }),
          ),
        }),
        mobileClinicAdvanced: z.object({
          amount: z.number().int().min(0),
          equipment: z.array(
            z.object({ name: z.string(), quantity: z.number().int().min(1) }),
          ),
        }),
        ambulanceServiceWithDriver: z.array(
          z.object({
            type1Amount: z.number().int().min(0),
            type2Amount: z.number().int().min(0),
            type3Amount: z.number().int().min(0),
            type1Equipment: z.array(
              z.object({ name: z.string(), quantity: z.number().int().min(1) }),
            ),
            type2Equipment: z.array(
              z.object({ name: z.string(), quantity: z.number().int().min(1) }),
            ),
            type3Equipment: z.array(
              z.object({ name: z.string(), quantity: z.number().int().min(1) }),
            ),
          }),
        ),
        ambulanceServiceWithoutDriver: z.array(
          z.object({
            type1Amount: z.number().int().min(0),
            type2Amount: z.number().int().min(0),
            type3Amount: z.number().int().min(0),
            type1Equipment: z.array(
              z.object({ name: z.string(), quantity: z.number().int().min(1) }),
            ),
            type2Equipment: z.array(
              z.object({ name: z.string(), quantity: z.number().int().min(1) }),
            ),
            type3Equipment: z.array(
              z.object({ name: z.string(), quantity: z.number().int().min(1) }),
            ),
          }),
        ),
        ambulance4x4WithDriver: z.object({
          amount: z.number().int().min(0),
          equipment: z.array(
            z.object({ name: z.string(), quantity: z.number().int().min(1) }),
          ),
        }),
        ambulance4x4WithoutDriver: z.object({
          amount: z.number().int().min(0),
          equipment: z.array(
            z.object({ name: z.string(), quantity: z.number().int().min(1) }),
          ),
        }),
        paramedicServices: z.array(
          z.object({
            amount: z.number().int(),
            type: z.enum(['SPECIALIST', 'TECHNICIAN']),
          }),
        ),
        doctorServices: z.array(
          z.object({
            specialization: z.string().min(1, { message: 'التخصص مطلوب' }),
            amount: z.number().int().min(1),
          }),
        ),
        medicalEquipment: z.array(
          z.object({
            name: z.string(),
            quantity: z.number().int().min(1),
          }),
        ),
        proposalDeadline: z.string().min(1),
        bankGuaranteePercent: z.number().min(0).max(20),
        kpis: z.array(z.string().min(1)),
        paymentSchedule: z.object({
          paymentType: z.enum(['MONTHLY', 'DELIVERABLES']),
          milestones: z
            .array(
              z.object({
                name: z.string().optional(),
                percentage: z.number().min(0).max(100).multipleOf(5),
              }),
            )
            .min(1),
        }),
      })
      .superRefine((value, ctx) => {
        if (
          value.requestType === 'EQUIPMENT' &&
          value.medicalEquipment.length === 0
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['medicalEquipment'],
            message: 'يجب إضافة معدة واحدة على الأقل',
          });
        }
      }) as never,
  },
  defaultValues: formDefaults,
});

export type SectionKey =
  | 'ambulanceServiceWithDriver'
  | 'ambulanceServiceWithoutDriver'
  | 'ambulance4x4WithDriver'
  | 'ambulance4x4WithoutDriver'
  | 'mobileClinicBasic'
  | 'mobileClinicAdvanced'
  | 'paramedicServices'
  | 'nurseServices'
  | 'doctorServices'
  | 'medicalEquipment';

export type OpenSections = Record<SectionKey, boolean>;

export const SECTION_KEYS_BY_REQUEST_TYPE = {
  SERVICE: [
    'ambulanceServiceWithDriver',
    'ambulanceServiceWithoutDriver',
    'ambulance4x4WithDriver',
    'ambulance4x4WithoutDriver',
    'mobileClinicBasic',
    'mobileClinicAdvanced',
    'paramedicServices',
    'nurseServices',
    'doctorServices',
  ],
  EQUIPMENT: ['medicalEquipment'],
} as const satisfies Record<RequestType, readonly SectionKey[]>;

function otherRequestType(type: RequestType): RequestType {
  return type === 'SERVICE' ? 'EQUIPMENT' : 'SERVICE';
}

const emptyAmbulanceService = {
  type1Amount: 0,
  type2Amount: 0,
  type3Amount: 0,
  type1Equipment: [] as EquipmentItem[],
  type2Equipment: [] as EquipmentItem[],
  type3Equipment: [] as EquipmentItem[],
};

function hasAmbulanceServiceValues(
  services: typeof formDefaults.ambulanceServiceWithDriver,
) {
  return services.some(
    (s) => s.type1Amount > 0 || s.type2Amount > 0 || s.type3Amount > 0,
  );
}

function computeInitialOpenSections(
  initialValues: typeof formDefaults,
): OpenSections {
  const populated: OpenSections = {
    ambulanceServiceWithDriver: hasAmbulanceServiceValues(
      initialValues.ambulanceServiceWithDriver,
    ),
    ambulanceServiceWithoutDriver: hasAmbulanceServiceValues(
      initialValues.ambulanceServiceWithoutDriver,
    ),
    ambulance4x4WithDriver: initialValues.ambulance4x4WithDriver.amount > 0,
    ambulance4x4WithoutDriver:
      initialValues.ambulance4x4WithoutDriver.amount > 0,
    mobileClinicBasic: initialValues.mobileClinicBasic.amount > 0,
    mobileClinicAdvanced: initialValues.mobileClinicAdvanced.amount > 0,
    paramedicServices:
      initialValues.paramedicServices?.some((s) => s.amount > 0) ?? false,
    nurseServices:
      initialValues.nurseServices?.some((s) => s.amount > 0) ?? false,
    doctorServices:
      initialValues.doctorServices?.some((s) => s.amount > 0) ?? false,
    medicalEquipment: (initialValues.medicalEquipment?.length ?? 0) > 0,
  };

  for (const key of SECTION_KEYS_BY_REQUEST_TYPE[
    otherRequestType(initialValues.requestType)
  ]) {
    populated[key] = false;
  }
  return populated;
}

async function submitRequest(
  values: typeof formDefaults,
  openSections: OpenSections,
  draftId?: string,
) {
  await client.request('POST /requests', {
    ambulanceServiceWithDriver: openSections.ambulanceServiceWithDriver
      ? values.ambulanceServiceWithDriver.map((service) => ({
          type1Amount: service.type1Amount,
          type2Amount: service.type2Amount,
          type3Amount: service.type3Amount,
          type1Equipment: service.type1Equipment.filter(
            (e) => e.name.trim() && e.quantity > 0,
          ),
          type2Equipment: service.type2Equipment.filter(
            (e) => e.name.trim() && e.quantity > 0,
          ),
          type3Equipment: service.type3Equipment.filter(
            (e) => e.name.trim() && e.quantity > 0,
          ),
        }))
      : [emptyAmbulanceService],
    ambulanceServiceWithoutDriver: openSections.ambulanceServiceWithoutDriver
      ? values.ambulanceServiceWithoutDriver.map((service) => ({
          type1Amount: service.type1Amount,
          type2Amount: service.type2Amount,
          type3Amount: service.type3Amount,
          type1Equipment: service.type1Equipment.filter(
            (e) => e.name.trim() && e.quantity > 0,
          ),
          type2Equipment: service.type2Equipment.filter(
            (e) => e.name.trim() && e.quantity > 0,
          ),
          type3Equipment: service.type3Equipment.filter(
            (e) => e.name.trim() && e.quantity > 0,
          ),
        }))
      : [emptyAmbulanceService],
    ambulance4x4WithDriver: openSections.ambulance4x4WithDriver
      ? {
          amount: values.ambulance4x4WithDriver.amount,
          equipment: values.ambulance4x4WithDriver.equipment.filter(
            (e) => e.name.trim() && e.quantity > 0,
          ),
        }
      : { amount: 0, equipment: [] },
    ambulance4x4WithoutDriver: openSections.ambulance4x4WithoutDriver
      ? {
          amount: values.ambulance4x4WithoutDriver.amount,
          equipment: values.ambulance4x4WithoutDriver.equipment.filter(
            (e) => e.name.trim() && e.quantity > 0,
          ),
        }
      : { amount: 0, equipment: [] },
    mobileClinicServices: [
      ...(openSections.mobileClinicBasic && values.mobileClinicBasic.amount > 0
        ? [
            {
              type: 'BASIC' as const,
              amount: values.mobileClinicBasic.amount,
              equipment: values.mobileClinicBasic.equipment.filter(
                (e) => e.name.trim() && e.quantity > 0,
              ),
            },
          ]
        : []),
      ...(openSections.mobileClinicAdvanced &&
      values.mobileClinicAdvanced.amount > 0
        ? [
            {
              type: 'ADVANCED' as const,
              amount: values.mobileClinicAdvanced.amount,
              equipment: values.mobileClinicAdvanced.equipment.filter(
                (e) => e.name.trim() && e.quantity > 0,
              ),
            },
          ]
        : []),
    ],
    doctorServices: openSections.doctorServices
      ? values.doctorServices
          .filter((s) => s.amount > 0)
          .map((service) => ({
            specialization: service.specialization,
            amount: service.amount,
          }))
      : [],
    housingTransportProvided: values.housingTransportProvided,
    isolatedWorkArea: values.isolatedWorkArea,
    mealsProvided: values.mealsProvided,
    notes: values.notes,
    dates: {
      from: values.dates.from,
      to: values.dates.to,
    },
    hours: {
      from: toScheduleTime(values.hours.from),
      to: toScheduleTime(values.hours.to),
    },
    location: values.location,
    paramedicServices: openSections.paramedicServices
      ? values.paramedicServices
          .filter((s) => s.amount > 0)
          .map((service) => ({
            amount: service.amount,
            type: service.type as never,
          }))
      : [],
    nurseServices: openSections.nurseServices
      ? values.nurseServices
          .filter((s) => s.amount > 0)
          .map((service) => ({
            amount: service.amount,
            type: service.type as never,
          }))
      : [],
    projectName: values.projectName,
    projectType: values.projectType,
    proposalDeadline: values.proposalDeadline,
    ...(values.bankGuaranteePercent > 0
      ? { bankGuaranteePercent: values.bankGuaranteePercent }
      : {}),
    kpis: values.kpis.filter((k) => k.trim()),
    medicalEquipment: openSections.medicalEquipment
      ? values.medicalEquipment.filter((e) => e.name.trim() && e.quantity > 0)
      : [],
    paymentSchedule: {
      paymentType: values.paymentSchedule.paymentType,
      milestones: values.paymentSchedule.milestones,
    },
    ...(draftId ? { draftId } : {}),
  });
}

function mergeDraftValues(data?: Record<string, unknown>) {
  const values = { ...formDefaults, ...data } as typeof formDefaults;
  const legacyMobileClinic = data?.mobileClinic;
  if (
    typeof legacyMobileClinic === 'number' &&
    legacyMobileClinic > 0 &&
    !data?.mobileClinicBasic
  ) {
    values.mobileClinicBasic = { amount: legacyMobileClinic, equipment: [] };
  }
  if (!values.paymentSchedule.milestones?.length) {
    values.paymentSchedule.milestones = [
      { percentage: MIN_FIRST_PAYMENT_PERCENT },
    ];
  }
  values.requestType ??= 'SERVICE';
  return values;
}

export async function requestEditorLoader({
  params,
}: {
  params: { draftId?: string };
}) {
  const identity = await requiresAuth();
  if (!identity.approved) {
    toast.info(i18n.t('errors.accountUnderReview'));
    throw redirect('/');
  }

  const draftId = params.draftId;
  if (!draftId) {
    return {
      draftId: undefined,
      initialValues: formDefaults,
      cacheKey: 'new',
    };
  }

  const localDraft = getLocalDraft(draftId);
  if (localDraft?.syncState === 'pending-create') {
    return {
      draftId,
      initialValues: mergeDraftValues(localDraft.data),
      cacheKey: `${draftId}:${localDraft.updatedAt}`,
    };
  }

  try {
    const serverDraft = await client.request('GET /me/request-drafts/{id}', {
      id: draftId,
    });

    if (
      localDraft &&
      new Date(localDraft.updatedAt) > new Date(serverDraft.updatedAt)
    ) {
      return {
        draftId,
        initialValues: mergeDraftValues(localDraft.data),
        cacheKey: `${draftId}:${localDraft.updatedAt}`,
      };
    }

    return {
      draftId,
      initialValues: mergeDraftValues(
        serverDraft.data as Record<string, unknown>,
      ),
      cacheKey: `${draftId}:${serverDraft.updatedAt}`,
    };
  } catch (error) {
    if (error instanceof APIError && error.status === 404) {
      clearLocalDraft(draftId);
      throw redirect('/requests');
    }

    throw error;
  }
}

type OpenSectionsContextValue = {
  openSections: OpenSections;
  setSectionOpen: (key: SectionKey, open: boolean) => void;
};

const OpenSectionsContext =
  React.createContext<OpenSectionsContextValue | null>(null);

function useOpenSections() {
  const context = React.use(OpenSectionsContext);
  if (!context) {
    throw new Error('useOpenSections must be used inside RequestForm');
  }
  return context;
}

function useSectionToggle(key: SectionKey) {
  const { openSections, setSectionOpen } = useOpenSections();
  const onOpenChange = React.useCallback(
    (open: boolean) => setSectionOpen(key, open),
    [setSectionOpen, key],
  );
  return { open: openSections[key], onOpenChange };
}

type ActiveSectionContextValue = {
  activeSection: PreviewSectionId;
  setActiveSection: (id: PreviewSectionId) => void;
};

const ActiveSectionContext =
  React.createContext<ActiveSectionContextValue | null>(null);

function useActiveSection() {
  const context = React.use(ActiveSectionContext);
  if (!context) {
    throw new Error('useActiveSection must be used inside RequestForm');
  }
  return context;
}

function PreviewAnchor({
  id,
  children,
}: React.PropsWithChildren<{ id: PreviewSectionId }>) {
  const { setActiveSection } = useActiveSection();
  return (
    <div id={id} onFocusCapture={() => setActiveSection(id)}>
      {children}
    </div>
  );
}

function SaveStatusHint({ status }: { status: DraftSaveStatus }) {
  const { t } = useTranslation();
  if (status === 'idle') return null;
  return (
    <span className="text-muted-foreground text-sm font-normal">
      {status === 'saving' ? t('newRequest.saving') : t('newRequest.saved')}
    </span>
  );
}

function formatDate(date: Date | string | undefined) {
  if (!date) {
    return '';
  }
  date = typeof date === 'string' ? new Date(date) : date;

  return date.toLocaleDateString(i18n.language, {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
}

function isValidDate(date: Date | undefined) {
  if (!date) {
    return false;
  }
  return !isNaN(date.getTime());
}

const ARABIC_WEEKDAY_LETTERS = ['ح', 'ن', 'ث', 'ر', 'خ', 'ج', 'س'];
const ENGLISH_WEEKDAY_LETTERS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const calendarFormatters = {
  formatWeekdayName: (date: Date) => {
    const letters = i18n.language.startsWith('ar')
      ? ARABIC_WEEKDAY_LETTERS
      : ENGLISH_WEEKDAY_LETTERS;
    return letters[date.getDay()];
  },
};

export function DatePicker({
  label,
  value,
  onChange,
  disabled,
  defaultMonth,
}: {
  label: string;
  value?: DateRange;
  onChange: (value: DateRange) => void;
  disabled?: (date: Date) => boolean;
  defaultMonth?: Date;
}) {
  const [open, setOpen] = React.useState(false);
  const id = React.useId();
  return (
    <div className="flex flex-col gap-3">
      <Label htmlFor={id} className="px-1">
        {label}
      </Label>
      <div className="relative flex gap-2">
        <Input
          id={id}
          value={
            value ? formatDate(value.from) + ' - ' + formatDate(value.to) : ''
          }
          className="bg-background pe-10"
          onChange={(e) => {
            const date = new Date(e.target.value);
            if (isValidDate(date)) {
              onChange({ from: date, to: date });
            }
          }}
          onClick={() => {
            setOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setOpen(true);
            }
          }}
        />
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              id="date-picker"
              variant="ghost"
              className="absolute end-0 top-1/2 -translate-y-1/2"
            >
              <CalendarIcon />
              <span className="sr-only">Select date</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="w-auto overflow-hidden p-0"
            align="end"
            alignOffset={-8}
            sideOffset={10}
          >
            <Calendar
              locale={i18n.language.startsWith('ar') ? arSA : enUS}
              ISOWeek={true}
              mode="range"
              captionLayout="label"
              formatters={calendarFormatters}
              selected={value}
              disabled={disabled}
              defaultMonth={defaultMonth}
              onSelect={(range) => {
                range && onChange({ from: range.from!, to: range.to! });
              }}
            />
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}

function SingleDatePicker({
  label,
  value,
  onChange,
  disabled,
}: {
  label: string;
  value?: Date;
  onChange: (value: Date) => void;
  disabled?: (date: Date) => boolean;
}) {
  const [open, setOpen] = React.useState(false);
  const id = React.useId();
  return (
    <div className="flex flex-col gap-3">
      {label && (
        <Label htmlFor={id} className="px-1">
          {label}
        </Label>
      )}
      <div className="relative flex gap-2">
        <Input
          id={id}
          value={value ? formatDate(value) : ''}
          className="bg-background pe-10"
          readOnly
          onClick={() => {
            setOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setOpen(true);
            }
          }}
        />
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              className="absolute end-0 top-1/2 -translate-y-1/2"
            >
              <CalendarIcon />
              <span className="sr-only">Select date</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="w-auto overflow-hidden p-0"
            align="end"
            alignOffset={-8}
            sideOffset={10}
          >
            <Calendar
              locale={i18n.language.startsWith('ar') ? arSA : enUS}
              ISOWeek={true}
              mode="single"
              captionLayout="label"
              formatters={calendarFormatters}
              selected={value}
              disabled={disabled}
              onSelect={(date) => {
                if (date) {
                  onChange(date);
                  setOpen(false);
                }
              }}
            />
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}

const ProjectIdentityFields = withForm({
  ...formOpts,
  render: function Render({ form }) {
    const { t } = useTranslation();
    return (
      <>
        <form.AppField
          name="projectName"
          children={(field) => (
            <field.TextField label={t('newRequest.projectName')} />
          )}
        />
        <form.Field
          name="projectType"
          children={(field) => (
            <div className="my-4 space-y-2">
              <Label>{t('newRequest.projectEntity')}</Label>
              <ToggleGroup
                type="single"
                className="w-full gap-x-2 border p-0.5"
                value={field.state.value}
                onValueChange={(value) => {
                  if (value) {
                    field.handleChange(value as 'GOVERNMENT' | 'PRIVATE');
                  }
                }}
              >
                <ToggleGroupItem
                  value="GOVERNMENT"
                  className="data-[state=on]:border-primary data-[state=on]:bg-primary/20 w-full rounded border border-transparent"
                >
                  {t('newRequest.government')}
                </ToggleGroupItem>
                <ToggleGroupItem
                  value="PRIVATE"
                  className="data-[state=on]:border-primary data-[state=on]:bg-primary/20 w-full rounded border border-transparent"
                >
                  {t('newRequest.private')}
                </ToggleGroupItem>
              </ToggleGroup>
              <FieldMessage errors={field.state.meta.errors} />
            </div>
          )}
        />
      </>
    );
  },
});

const AMBULANCE_TYPE_LABELS = [
  {
    field: 'type1Amount',
    equipmentField: 'type1Equipment',
    typeKey: 'type1' as const,
  },
  {
    field: 'type2Amount',
    equipmentField: 'type2Equipment',
    typeKey: 'type2' as const,
  },
  {
    field: 'type3Amount',
    equipmentField: 'type3Equipment',
    typeKey: 'type3' as const,
  },
] as const;

const AmbulanceServiceSection = withForm({
  ...formOpts,
  props: {
    fieldName: 'ambulanceServiceWithDriver' as
      | 'ambulanceServiceWithDriver'
      | 'ambulanceServiceWithoutDriver',
    title: '',
  },
  render: function Render({ form, fieldName, title }) {
    const { t } = useTranslation();
    const isValid = useStore(
      form.store,
      (state) => state.fieldMeta[fieldName]?.isValid,
    );
    const section = useSectionToggle(fieldName);

    return (
      <div className="w-full space-y-2">
        <ExpandableCheckbox
          title={title}
          className={cn('mb-4', !isValid ? 'border-destructive' : '')}
          {...section}
        >
          {AMBULANCE_TYPE_LABELS.map(({ field, equipmentField, typeKey }) => (
            <div key={field} className="mb-4 rounded-lg border p-3">
              <form.Field
                name={`${fieldName}[0].${field}`}
                children={(formField) => (
                  <div className="mb-3">
                    <CounterField
                      label={
                        <span className="inline-flex items-center gap-1">
                          {AMBULANCE_SERVICE_TYPES[typeKey].name}
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Info className="text-muted-foreground size-4 cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent>
                              {
                                AMBULANCE_SERVICE_TYPES[typeKey]
                                  .arabicDescription
                              }
                            </TooltipContent>
                          </Tooltip>
                        </span>
                      }
                      className="mb-2"
                      value={formField.state.value as never}
                      onChange={formField.handleChange}
                    />
                    <FieldMessage errors={formField.state.meta.errors} />
                  </div>
                )}
              />
              <form.Field
                name={`${fieldName}[0].${equipmentField}`}
                children={(equipmentArrayField) => (
                  <div className="space-y-2">
                    <EquipmentListField
                      label={t('newRequest.specifiedEquipment')}
                      value={equipmentArrayField.state.value as EquipmentItem[]}
                      onChange={equipmentArrayField.handleChange}
                    />
                    <FieldMessage
                      errors={equipmentArrayField.state.meta.errors}
                    />
                  </div>
                )}
              />
            </div>
          ))}
        </ExpandableCheckbox>
        <form.Field
          name={fieldName}
          children={(field) => (
            <FieldMessage className="mb-4" errors={field.state.meta.errors} />
          )}
        />
      </div>
    );
  },
});

const AmountWithEquipmentSection = withForm({
  ...formOpts,
  props: {
    fieldName: 'ambulance4x4WithDriver' as
      | 'ambulance4x4WithDriver'
      | 'ambulance4x4WithoutDriver'
      | 'mobileClinicBasic'
      | 'mobileClinicAdvanced',
    title: '',
  },
  render: function Render({ form, fieldName, title }) {
    const { t } = useTranslation();
    const section = useSectionToggle(fieldName);

    return (
      <ExpandableCheckbox title={title} className="mb-4" {...section}>
        <form.Field
          name={`${fieldName}.amount`}
          children={(field) => (
            <div className="mb-3">
              <CounterField
                label={t('newRequest.count')}
                value={field.state.value}
                onChange={field.handleChange}
              />
              <FieldMessage errors={field.state.meta.errors} />
            </div>
          )}
        />
        <form.Field
          name={`${fieldName}.equipment`}
          children={(equipmentField) => (
            <div className="space-y-2">
              <EquipmentListField
                label={t('newRequest.specifiedEquipment')}
                value={equipmentField.state.value as EquipmentItem[]}
                onChange={equipmentField.handleChange}
              />
              <FieldMessage errors={equipmentField.state.meta.errors} />
            </div>
          )}
        />
      </ExpandableCheckbox>
    );
  },
});

const ServiceTypesFieldset = withForm({
  ...formOpts,
  render: function Render({ form }) {
    const { t } = useTranslation();
    const requestType = useStore(
      form.store,
      (state) => state.values.requestType,
    );
    return (
      <RequestSectionGroup enabled={requestType === 'SERVICE'}>
        <p className="my-6">{t('newRequest.serviceType')}</p>
        <AmbulanceServiceSection
          form={form}
          fieldName="ambulanceServiceWithDriver"
          title={t('newRequest.ambulanceWithDriver')}
        />
        <AmbulanceServiceSection
          form={form}
          fieldName="ambulanceServiceWithoutDriver"
          title={t('newRequest.ambulanceWithoutDriver')}
        />
        <AmountWithEquipmentSection
          form={form}
          fieldName="ambulance4x4WithDriver"
          title={t('newRequest.ambulance4x4WithDriver')}
        />
        <AmountWithEquipmentSection
          form={form}
          fieldName="ambulance4x4WithoutDriver"
          title={t('newRequest.ambulance4x4WithoutDriver')}
        />
        <AmountWithEquipmentSection
          form={form}
          fieldName="mobileClinicBasic"
          title={t('newRequest.basicClinic')}
        />
        <AmountWithEquipmentSection
          form={form}
          fieldName="mobileClinicAdvanced"
          title={t('newRequest.advancedClinic')}
        />
      </RequestSectionGroup>
    );
  },
});

const ProjectSection = withForm({
  ...formOpts,
  render: function Render({ form }) {
    const { t } = useTranslation();
    return (
      <PreviewAnchor id="project">
        <Section title={t('newRequest.serviceDetails')} />
        <ProjectIdentityFields form={form} />
        <ServiceTypesFieldset form={form} />
      </PreviewAnchor>
    );
  },
});

const ParamedicServiceForm = withForm({
  ...formOpts,
  render: function Render({ form }) {
    const { t } = useTranslation();
    const isValid = useStore(
      form.store,
      (state) => state.fieldMeta.paramedicServices?.isValid,
    );
    const section = useSectionToggle('paramedicServices');

    return (
      <ExpandableCheckbox
        className={cn('mb-4', !isValid ? 'border-destructive' : '')}
        title={t('newRequest.paramedic')}
        subtitle={t('newRequest.paramedicSubtitle')}
        {...section}
      >
        <form.AppField
          name="paramedicServices[0]"
          children={(field) => {
            return (
              <>
                <CounterField
                  className="mb-4"
                  label={t('newRequest.paramedicSpecialist')}
                  value={field.state.value.amount}
                  onChange={(value) => {
                    field.handleChange({ amount: value, type: 'SPECIALIST' });
                  }}
                />
                <FieldMessage errors={field.state.meta.errors} />
              </>
            );
          }}
        />
        <form.AppField
          name="paramedicServices[1]"
          children={(field) => {
            return (
              <>
                <CounterField
                  className="mb-4"
                  label={t('newRequest.paramedicTechnician')}
                  value={field.state.value.amount}
                  onChange={(v) => {
                    field.handleChange({
                      amount: v,
                      type: 'TECHNICIAN',
                    });
                  }}
                />
                <FieldMessage errors={field.state.meta.errors} />
              </>
            );
          }}
        />
      </ExpandableCheckbox>
    );
  },
});

const NurseServiceForm = withForm({
  ...formOpts,
  render: function Render({ form }) {
    const { t } = useTranslation();
    const isValid = useStore(
      form.store,
      (state) => state.fieldMeta.nurseServices?.isValid,
    );
    const section = useSectionToggle('nurseServices');

    return (
      <ExpandableCheckbox
        title={t('newRequest.nurses')}
        subtitle={t('newRequest.nursesSubtitle')}
        className={cn('mb-4', !isValid ? 'border-destructive' : '')}
        {...section}
      >
        <form.AppField
          name="nurseServices[0]"
          children={(field) => {
            return (
              <>
                <CounterField
                  className="mb-4"
                  label={t('newRequest.nurseSpecialist')}
                  value={field.state.value.amount}
                  onChange={(v) => {
                    field.handleChange({ amount: v, type: 'SPECIALIST' });
                  }}
                />
                <FieldMessage errors={field.state.meta.errors} />
              </>
            );
          }}
        />
        <form.AppField
          name="nurseServices[1]"
          children={(field) => {
            return (
              <>
                <CounterField
                  className="mb-4"
                  label={t('newRequest.nurseTechnician')}
                  value={field.state.value.amount}
                  onChange={(v) => {
                    field.handleChange({ amount: v, type: 'TECHNICIAN' });
                  }}
                />
                <FieldMessage errors={field.state.meta.errors} />
              </>
            );
          }}
        />
      </ExpandableCheckbox>
    );
  },
});

const DoctorServiceForm = withForm({
  ...formOpts,
  render: function Render({ form }) {
    const { t } = useTranslation();
    const isValid = useStore(
      form.store,
      (state) => state.fieldMeta.doctorServices?.isValid,
    );
    const section = useSectionToggle('doctorServices');

    return (
      <ExpandableCheckbox
        title={t('newRequest.doctors')}
        subtitle={t('newRequest.doctorsSubtitle')}
        className={cn('mb-4', !isValid ? 'border-destructive' : '')}
        {...section}
      >
        <form.Field name="doctorServices" mode="array">
          {(field) => (
            <div className="space-y-4">
              {field.state.value.map((_, index) => (
                <div
                  key={index}
                  className="flex items-start gap-2 rounded-lg border p-3"
                >
                  <div className="flex-1 space-y-3">
                    <form.Field
                      name={`doctorServices[${index}].specialization`}
                      children={(subField) => (
                        <div>
                          <Input
                            placeholder={t(
                              'newRequest.specializationPlaceholder',
                            )}
                            value={subField.state.value}
                            onChange={(e) =>
                              subField.handleChange(e.target.value)
                            }
                            onBlur={subField.handleBlur}
                          />
                          <FieldMessage errors={subField.state.meta.errors} />
                        </div>
                      )}
                    />
                    <form.Field
                      name={`doctorServices[${index}].amount`}
                      children={(subField) => (
                        <div>
                          <CounterField
                            label={t('newRequest.count')}
                            value={subField.state.value}
                            onChange={subField.handleChange}
                          />
                          <FieldMessage errors={subField.state.meta.errors} />
                        </div>
                      )}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="text-destructive hover:text-destructive/80 shrink-0"
                    onClick={() => field.removeValue(index)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                className="mb-2 w-full"
                onClick={() =>
                  field.pushValue({ specialization: '', amount: 1 })
                }
              >
                <Plus className="h-4 w-4" />
                {t('newRequest.addDoctor')}
              </Button>
            </div>
          )}
        </form.Field>
      </ExpandableCheckbox>
    );
  },
});

const MedicalStaffSection = withForm({
  ...formOpts,
  render: function Render({ form }) {
    const { t } = useTranslation();
    const requestType = useStore(
      form.store,
      (state) => state.values.requestType,
    );
    return (
      <PreviewAnchor id="medical">
        <RequestSectionGroup enabled={requestType === 'SERVICE'}>
          <p className="my-6">{t('newRequest.medicalStaffTypes')}</p>
          <form.AppField
            name="paramedicServices"
            children={() => <ParamedicServiceForm form={form} />}
          />
          <form.AppField
            name="nurseServices"
            children={() => <NurseServiceForm form={form} />}
          />
          <form.AppField
            name="doctorServices"
            children={() => <DoctorServiceForm form={form} />}
          />
        </RequestSectionGroup>
      </PreviewAnchor>
    );
  },
});

const MedicalEquipmentForm = withForm({
  ...formOpts,
  render: function Render({ form }) {
    const { t } = useTranslation();
    const section = useSectionToggle('medicalEquipment');

    return (
      <ExpandableCheckbox
        title={t('newRequest.medicalEquipment')}
        subtitle={t('newRequest.medicalEquipmentSubtitle')}
        className="mb-4"
        {...section}
      >
        <form.Field name="medicalEquipment" mode="array">
          {(field) => (
            <div className="space-y-3">
              {field.state.value.map((_, index) => (
                <div
                  key={index}
                  className="flex items-start gap-2 rounded-lg border p-3"
                >
                  <div className="flex-1 space-y-3">
                    <form.Field
                      name={`medicalEquipment[${index}].name`}
                      children={(subField) => (
                        <div>
                          <Input
                            placeholder={t(
                              'newRequest.equipmentNamePlaceholder',
                            )}
                            value={subField.state.value}
                            onChange={(e) =>
                              subField.handleChange(e.target.value)
                            }
                            onBlur={subField.handleBlur}
                          />
                          <FieldMessage errors={subField.state.meta.errors} />
                        </div>
                      )}
                    />
                    <form.Field
                      name={`medicalEquipment[${index}].quantity`}
                      children={(subField) => (
                        <div>
                          <CounterField
                            label={t('newRequest.quantity')}
                            value={subField.state.value}
                            onChange={subField.handleChange}
                          />
                          <FieldMessage errors={subField.state.meta.errors} />
                        </div>
                      )}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="text-destructive hover:text-destructive/80 shrink-0"
                    onClick={() => field.removeValue(index)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => field.pushValue({ name: '', quantity: 1 })}
              >
                <Plus className="h-4 w-4" />
                {t('newRequest.addEquipment')}
              </Button>
            </div>
          )}
        </form.Field>
      </ExpandableCheckbox>
    );
  },
});

const MedicalEquipmentSection = withForm({
  ...formOpts,
  render: function Render({ form }) {
    const { t } = useTranslation();
    const requestType = useStore(
      form.store,
      (state) => state.values.requestType,
    );
    return (
      <RequestSectionGroup enabled={requestType === 'EQUIPMENT'}>
        <Section title={t('newRequest.medicalEquipment')} />
        <form.AppField
          name="medicalEquipment"
          children={() => <MedicalEquipmentForm form={form} />}
        />
      </RequestSectionGroup>
    );
  },
});

const BookingDatesField = withForm({
  ...formOpts,
  render: function Render({ form }) {
    const { t } = useTranslation();
    return (
      <form.Field
        name="dates"
        children={(field) => (
          <div className="space-y-2">
            <DatePicker
              label={t('newRequest.bookingPeriod')}
              value={
                field.state.value.from
                  ? {
                      from: new Date(field.state.value.from),
                      to: new Date(field.state.value.to),
                    }
                  : undefined
              }
              defaultMonth={startOfTomorrow()}
              disabled={(date) => isBefore(date, startOfTomorrow())}
              onChange={(value) => {
                field.handleChange({
                  from: value.from?.toISOString()!,
                  to: value.to?.toISOString()!,
                });
              }}
            />
            <FieldMessage errors={field.state.meta.errors} />
          </div>
        )}
      />
    );
  },
});

const BookingHoursField = withForm({
  ...formOpts,
  render: function Render({ form }) {
    return (
      <form.Field
        name="hours"
        children={(field) => (
          <>
            <HoursField
              className="mt-4 mb-2"
              onChange={field.handleChange}
              value={field.state.value}
            />
            <FieldMessage errors={field.state.meta.errors} />
          </>
        )}
      />
    );
  },
});

const BookingDurationSummary = withForm({
  ...formOpts,
  render: function Render({ form }) {
    const { t } = useTranslation();
    const hours = useStore(form.store, (state) => state.values.hours);
    const dates = useStore(form.store, (state) => state.values.dates);

    const fromDate = dates.from ? new Date(dates.from) : undefined;
    const toDate = dates.to ? new Date(dates.to) : undefined;

    // Inclusive day distance: add 1 day to the end date to emulate inclusive range
    const dayDistance =
      fromDate && toDate
        ? formatDistanceStrict(addDays(toDate, 1), fromDate, {
            unit: 'day',
            locale: ar,
          })
        : undefined;

    const hoursDistance =
      hours.from && hours.to && hours.from !== hours.to
        ? `${t('newRequest.workHoursPerDay')} ${formatDistanceStrict(
            hours.to,
            hours.from,
            {
              unit: 'hour',
              locale: ar,
            },
          )}`
        : undefined;

    const relativeTitle =
      fromDate && toDate
        ? `من ${formatRelative(fromDate, new Date(), { locale: ar })} إلى ${formatRelative(toDate, new Date(), { locale: ar })}`
        : undefined;

    return (
      <div
        className="mb-6 flex items-center gap-2.5 rounded-lg bg-[#FFF7E7] px-4 py-3"
        title={relativeTitle}
      >
        <p className="text-sm font-medium">{t('newRequest.bookingDuration')}</p>
        {dayDistance && (
          <p className="rounded border border-[#FFD581] bg-[#FFEDCA] px-2 py-1 text-xs font-medium">
            {dayDistance}
          </p>
        )}
        {hoursDistance && (
          <p className="rounded border border-[#FFD581] bg-[#FFEDCA] px-2 py-1 text-xs font-medium">
            {hoursDistance}
          </p>
        )}
      </div>
    );
  },
});

const BookingLocationField = withForm({
  ...formOpts,
  render: function Render({ form }) {
    return (
      <form.Field
        name="location"
        children={(field) => (
          <div className="space-y-2">
            <APIProvider
              apiKey={'AIzaSyBezbDkE1VSHfNsZHLy47kPXeSXgvjchVc'}
              language="ar"
              region="SA"
            >
              <SearchPlaces
                onChange={(place) => {
                  field.handleChange({
                    latitude: place.geometry.location.lat(),
                    longitude: place.geometry.location.lng(),
                    name: place.formatted_address || JSON.stringify(place.name),
                  });
                }}
              />
              <Map
                style={{
                  width: '100%',
                  height: '160px',
                  marginTop: '16px',
                  marginBottom: '16px',
                  borderRadius: '8px',
                }}
                keyboardShortcuts={false}
                defaultCenter={{ lat: 23.8859, lng: 45.0792 }}
                reuseMaps={true}
                defaultZoom={6}
                gestureHandling="none"
                disableDefaultUI
              />
            </APIProvider>
            <FieldMessage className="mb-4" errors={field.state.meta.errors} />
          </div>
        )}
      />
    );
  },
});

const BookingSection = withForm({
  ...formOpts,
  render: function Render({ form }) {
    const { t } = useTranslation();
    return (
      <PreviewAnchor id="booking">
        <Section title={t('newRequest.bookingPeriodLocation')} />
        <BookingDatesField form={form} />
        <BookingHoursField form={form} />
        <BookingDurationSummary form={form} />
        <BookingLocationField form={form} />
      </PreviewAnchor>
    );
  },
});

const ProposalDeadlineField = withForm({
  ...formOpts,
  render: function Render({ form }) {
    const { t } = useTranslation();
    const dates = useStore(form.store, (state) => state.values.dates);

    return (
      <form.Field
        name="proposalDeadline"
        children={(field) => (
          <div className="space-y-2">
            <SingleDatePicker
              label={t('newRequest.proposalDeadline')}
              value={
                field.state.value ? new Date(field.state.value) : undefined
              }
              onChange={(date) => {
                field.handleChange(date.toISOString());
              }}
              disabled={(date) =>
                isBefore(date, startOfToday()) ||
                (dates.from ? date >= new Date(dates.from) : false)
              }
            />
            <FieldMessage errors={field.state.meta.errors} />
          </div>
        )}
      />
    );
  },
});

const ShortTermSplitPicker = withForm({
  ...formOpts,
  render: function Render({ form }) {
    const { t } = useTranslation();
    return (
      <form.Field
        name="paymentSchedule"
        children={(field) => {
          const { milestones } = field.state.value;
          const selectedSplitKey =
            milestones.length === 2
              ? `${milestones[0].percentage}-${milestones[1].percentage}`
              : '';

          return (
            <div className="space-y-2">
              <Label className="text-sm font-normal">
                {t('newRequest.selectPaymentSplit')}
              </Label>
              <ToggleGroup
                type="single"
                className="w-full gap-x-2"
                value={selectedSplitKey}
                onValueChange={(val) => {
                  if (!val) return;
                  const [first, second] = val.split('-').map(Number);
                  field.handleChange({
                    ...field.state.value,
                    milestones: [{ percentage: first }, { percentage: second }],
                  });
                }}
              >
                {SHORT_TERM_SPLIT_OPTIONS.map(([first, second]) => (
                  <ToggleGroupItem
                    key={`${first}-${second}`}
                    value={`${first}-${second}`}
                    className="data-[state=on]:border-primary data-[state=on]:bg-primary/20 flex-1 rounded border border-transparent"
                  >
                    {first}-{second}
                  </ToggleGroupItem>
                ))}
              </ToggleGroup>
            </div>
          );
        }}
      />
    );
  },
});

const PaymentTypeToggle = withForm({
  ...formOpts,
  render: function Render({ form }) {
    const { t } = useTranslation();
    return (
      <form.Field
        name="paymentSchedule"
        children={(field) => (
          <>
            <Label>{t('newRequest.paymentSchedule')}</Label>
            <ToggleGroup
              type="single"
              className="w-full gap-x-2 border p-0.5"
              value={field.state.value.paymentType}
              onValueChange={(val) => {
                if (val === 'MONTHLY') {
                  field.handleChange({
                    paymentType: 'MONTHLY',
                    milestones: [{ percentage: MIN_FIRST_PAYMENT_PERCENT }],
                  });
                } else if (val === 'DELIVERABLES') {
                  field.handleChange({
                    paymentType: 'DELIVERABLES',
                    milestones: [
                      {
                        name: t('newRequest.firstPayment'),
                        percentage: MIN_FIRST_PAYMENT_PERCENT,
                      },
                    ],
                  });
                }
              }}
            >
              <ToggleGroupItem
                value="MONTHLY"
                className="data-[state=on]:border-primary data-[state=on]:bg-primary/20 flex-1 rounded border border-transparent"
              >
                {t('newRequest.monthly')}
              </ToggleGroupItem>
              <ToggleGroupItem
                value="DELIVERABLES"
                className="data-[state=on]:border-primary data-[state=on]:bg-primary/20 flex-1 rounded border border-transparent"
              >
                {t('newRequest.deliverables')}
              </ToggleGroupItem>
            </ToggleGroup>
          </>
        )}
      />
    );
  },
});

const MonthlyInstallments = withForm({
  ...formOpts,
  render: function Render({ form }) {
    const { t } = useTranslation();
    const dates = useStore(form.store, (state) => state.values.dates);

    return (
      <form.Field
        name="paymentSchedule"
        children={(field) => {
          const schedule = field.state.value;
          if (schedule.paymentType !== 'MONTHLY') return null;

          const firstPayment =
            schedule.milestones[0]?.percentage ?? MIN_FIRST_PAYMENT_PERCENT;
          const installments =
            dates.from && dates.to
              ? calculateMonthlyInstallments(dates.from, dates.to, firstPayment)
              : [];
          if (installments.length === 0) return null;

          return (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Label className="flex-1 text-sm font-normal">
                  {t('newRequest.firstPayment')}
                </Label>
                <Input
                  type="number"
                  min={MIN_FIRST_PAYMENT_PERCENT}
                  max={100}
                  step={5}
                  value={firstPayment}
                  onChange={(e) => {
                    const snapped = Math.round(Number(e.target.value) / 5) * 5;
                    const clamped = Math.max(
                      MIN_FIRST_PAYMENT_PERCENT,
                      Math.min(100, snapped),
                    );
                    field.handleChange({
                      ...schedule,
                      milestones: [{ percentage: clamped }],
                    });
                  }}
                  className="w-20"
                />
                <span className="text-muted-foreground text-sm">%</span>
              </div>
              <div className="space-y-2 rounded-lg border p-3">
                {installments.map((m, index) => (
                  <div
                    key={index}
                    className="text-muted-foreground flex items-center justify-between text-sm"
                  >
                    <span>
                      {t('shared.reviewRequest.installment')}{' '}
                      {toArabicOrdinal(index + 1, 'feminine')}
                    </span>
                    <span className="font-medium">{m.percentage}%</span>
                  </div>
                ))}
              </div>
            </div>
          );
        }}
      />
    );
  },
});

function sumMilestonePercentages(
  milestones: typeof formDefaults.paymentSchedule.milestones,
) {
  return milestones.reduce((sum, m) => sum + (m.percentage || 0), 0);
}

const DeliverableMilestones = withForm({
  ...formOpts,
  render: function Render({ form }) {
    const { t } = useTranslation();
    return (
      <form.Field
        name="paymentSchedule"
        children={(field) => {
          const schedule = field.state.value;
          if (schedule.paymentType !== 'DELIVERABLES') return null;

          const total = sumMilestonePercentages(schedule.milestones);
          const remaining = 100 - total;

          return (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-normal">
                  {t('newRequest.milestones')}
                </Label>
                <span
                  className={cn(
                    'text-sm font-medium',
                    Math.abs(total - 100) < 0.01
                      ? 'text-green-600'
                      : 'text-destructive',
                  )}
                >
                  {t('newRequest.total')} {total}%
                </span>
              </div>

              {schedule.milestones.map((milestone, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2 rounded-lg border p-3"
                >
                  <span className="text-muted-foreground shrink-0 text-sm">
                    {t('newRequest.deliverable')}{' '}
                    {toArabicOrdinal(index + 1, 'feminine')}:
                  </span>
                  <Input
                    value={milestone.name || ''}
                    disabled={index === 0}
                    onChange={(e) => {
                      const newMilestones = [...schedule.milestones];
                      newMilestones[index] = {
                        ...newMilestones[index],
                        name: e.target.value,
                      };
                      field.handleChange({
                        ...schedule,
                        milestones: newMilestones,
                      });
                    }}
                    className="flex-1"
                  />
                  <Input
                    type="number"
                    min={index === 0 ? MIN_FIRST_PAYMENT_PERCENT : 5}
                    max={100}
                    step={5}
                    value={milestone.percentage}
                    onChange={(e) => {
                      const newMilestones = [...schedule.milestones];
                      const snapped =
                        Math.round(Number(e.target.value) / 5) * 5;
                      const minVal =
                        index === 0 ? MIN_FIRST_PAYMENT_PERCENT : 5;
                      newMilestones[index] = {
                        ...newMilestones[index],
                        percentage: Math.max(minVal, Math.min(100, snapped)),
                      };
                      field.handleChange({
                        ...schedule,
                        milestones: newMilestones,
                      });
                    }}
                    className="w-20"
                  />
                  <span className="text-muted-foreground text-sm">%</span>
                  {index > 0 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="text-destructive hover:text-destructive/80 shrink-0"
                      onClick={() => {
                        field.handleChange({
                          ...schedule,
                          milestones: schedule.milestones.filter(
                            (_, i) => i !== index,
                          ),
                        });
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}

              {total < 100 && (
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    field.handleChange({
                      ...schedule,
                      milestones: [
                        ...schedule.milestones,
                        { name: '', percentage: Math.min(remaining, 30) },
                      ],
                    });
                  }}
                >
                  <Plus className="h-4 w-4" />
                  {t('newRequest.addMilestone')} ({remaining}
                  {t('newRequest.remaining')})
                </Button>
              )}

              {schedule.milestones.length < 2 && (
                <p className="text-destructive text-sm">
                  {t('newRequest.minTwoMilestones')}
                </p>
              )}
            </div>
          );
        }}
      />
    );
  },
});

const PaymentScheduleSection = withForm({
  ...formOpts,
  render: function Render({ form }) {
    const dates = useStore(form.store, (state) => state.values.dates);
    const shortTerm =
      dates.from && dates.to ? isShortTermProject(dates.from, dates.to) : false;

    const prevShortTermRef = React.useRef(shortTerm);
    React.useEffect(() => {
      if (prevShortTermRef.current === shortTerm) return;
      prevShortTermRef.current = shortTerm;

      if (shortTerm) {
        const [first, second] = SHORT_TERM_SPLIT_OPTIONS[0];
        form.setFieldValue('paymentSchedule', {
          paymentType: 'MONTHLY',
          milestones: [{ percentage: first }, { percentage: second }],
        });
      } else {
        form.setFieldValue('paymentSchedule', {
          paymentType: 'MONTHLY',
          milestones: [{ percentage: MIN_FIRST_PAYMENT_PERCENT }],
        });
      }
    }, [shortTerm, form]);

    return (
      <div className="my-4 space-y-4">
        <form.Field
          name="paymentSchedule"
          children={(field) => (
            <div className="space-y-4">
              <div className="space-y-4">
                {shortTerm ? (
                  <ShortTermSplitPicker form={form} />
                ) : (
                  <>
                    <PaymentTypeToggle form={form} />
                    <MonthlyInstallments form={form} />
                    <DeliverableMilestones form={form} />
                  </>
                )}
              </div>
              <FieldMessage errors={field.state.meta.errors} />
            </div>
          )}
        />
      </div>
    );
  },
});

const BankGuaranteeField = withForm({
  ...formOpts,
  render: function Render({ form }) {
    const { t } = useTranslation();
    return (
      <form.Field
        name="bankGuaranteePercent"
        children={(field) => (
          <div className="my-4 space-y-4 rounded border border-dashed p-4">
            <div className="flex items-center justify-between">
              <Label>{t('newRequest.bankGuaranteePercent')}</Label>
              <span className="text-sm font-medium">
                {field.state.value || 0}%
              </span>
            </div>
            <p className="text-muted-foreground text-sm">
              {t('newRequest.noBankGuarantee')}
            </p>
            <Slider
              min={0}
              max={20}
              step={10}
              dir="rtl"
              value={[field.state.value || 0]}
              onValueChange={(values) => field.handleChange(values[0])}
            />
            <div className="text-muted-foreground flex justify-between text-xs">
              <span>0%</span>
              <span>20%</span>
            </div>
            <FieldMessage errors={field.state.meta.errors} />
          </div>
        )}
      />
    );
  },
});

const KPIForm = withForm({
  ...formOpts,
  render: function Render({ form }) {
    const { t } = useTranslation();
    return (
      <div className="space-y-2">
        <Label>{t('newRequest.kpis')}</Label>
        <form.Field name="kpis" mode="array">
          {(field) => (
            <div className="space-y-3">
              {field.state.value.map((_, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2 rounded-lg border p-3"
                >
                  <form.Field
                    name={`kpis[${index}]`}
                    children={(subField) => (
                      <div className="flex-1">
                        <Input
                          placeholder={t('newRequest.kpiPlaceholder')}
                          value={subField.state.value}
                          onChange={(e) =>
                            subField.handleChange(e.target.value)
                          }
                          onBlur={subField.handleBlur}
                        />
                        <FieldMessage errors={subField.state.meta.errors} />
                      </div>
                    )}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="text-destructive hover:text-destructive/80 shrink-0"
                    onClick={() => field.removeValue(index)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => field.pushValue('')}
              >
                <Plus className="h-4 w-4" />
                {t('newRequest.addKpi')}
              </Button>
            </div>
          )}
        </form.Field>
        <p className="text-muted-foreground text-sm">
          {t('newRequest.kpiDescription')}
        </p>
      </div>
    );
  },
});

const ContractTermsSection = withForm({
  ...formOpts,
  render: function Render({ form }) {
    const { t } = useTranslation();
    return (
      <PreviewAnchor id="contract">
        <Section title={t('newRequest.contractPaymentTerms')} />
        <ProposalDeadlineField form={form} />
        <PaymentScheduleSection form={form} />
        <BankGuaranteeField form={form} />
        <form.AppField name="kpis" children={() => <KPIForm form={form} />} />
      </PreviewAnchor>
    );
  },
});

const YesNoFormField = withForm({
  ...formOpts,
  props: {
    name: 'mealsProvided' as
      | 'mealsProvided'
      | 'housingTransportProvided'
      | 'isolatedWorkArea',
    label: '',
  },
  render: function Render({ form, name, label }) {
    return (
      <form.Field
        name={name}
        children={(field) => (
          <div className="grid w-full items-center gap-2">
            <YesNoField
              className="my-4"
              label={label}
              value={field.state.value ? 'true' : 'false'}
              onValueChange={(val) => field.handleChange(val === 'true')}
            />
            <FieldMessage errors={field.state.meta.errors} />
          </div>
        )}
      />
    );
  },
});

const NotesField = withForm({
  ...formOpts,
  render: function Render({ form }) {
    return (
      <form.Field
        name="notes"
        children={(field) => (
          <>
            <Textarea
              value={field.state.value}
              onBlur={field.handleBlur}
              onChange={(e) => field.handleChange(e.target.value)}
            />
            <FieldMessage errors={field.state.meta.errors} />
          </>
        )}
      />
    );
  },
});

const AdditionalDetailsSection = withForm({
  ...formOpts,
  render: function Render({ form }) {
    const { t } = useTranslation();
    return (
      <PreviewAnchor id="additional">
        <Section title={t('newRequest.additionalDetails')} />
        <YesNoFormField
          form={form}
          name="isolatedWorkArea"
          label={t('newRequest.isolatedArea')}
        />
        <YesNoFormField
          form={form}
          name="housingTransportProvided"
          label={t('newRequest.housingTransport')}
        />
        <YesNoFormField
          form={form}
          name="mealsProvided"
          label={t('newRequest.mealsProvided')}
        />
        <Label className="mb-4">
          {t('newRequest.additionalNotes')}
          <span className="text-muted-foreground text-sm">
            {t('newRequest.optional')}
          </span>
        </Label>
        <NotesField form={form} />
      </PreviewAnchor>
    );
  },
});

const RequestPreviewPanel = withForm({
  ...formOpts,
  render: function Render({ form }) {
    const [isOpen, setIsOpen] = React.useState(false);
    const { activeSection } = useActiveSection();
    const { openSections } = useOpenSections();
    const requestType = useStore(
      form.store,
      (state) => state.values.requestType,
    );

    return (
      <RequestPreview
        form={form}
        isOpen={isOpen}
        onToggle={() => setIsOpen(!isOpen)}
        activeSection={activeSection}
        openSections={openSections}
        requestType={requestType}
      />
    );
  },
});

export default function RequestPage() {
  const loaderData = useLoaderData() as Awaited<
    ReturnType<typeof requestEditorLoader>
  >;
  const navigate = useNavigate();

  const handleDraftCreated = React.useCallback(
    (createdDraftId: string) => {
      navigate(`/requests/drafts/${createdDraftId}`, { replace: true });
    },
    [navigate],
  );

  return (
    <RequestForm
      key={loaderData.cacheKey}
      draftId={loaderData.draftId}
      initialValues={loaderData.initialValues}
      onDraftCreated={handleDraftCreated}
    />
  );
}

function RequestForm({
  draftId,
  initialValues,
  onDraftCreated,
}: {
  draftId?: string;
  initialValues: typeof formDefaults;
  onDraftCreated: (draftId: string) => void;
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const prepareDraftForSubmitRef = React.useRef(
    async (_value: typeof formDefaults) => draftId,
  );
  const [openSections, setOpenSections] = React.useState<OpenSections>(() =>
    computeInitialOpenSections(initialValues),
  );
  const openSectionsRef = React.useRef(openSections);
  openSectionsRef.current = openSections;
  const setSectionOpen = React.useCallback((key: SectionKey, open: boolean) => {
    setOpenSections((prev) => ({ ...prev, [key]: open }));
  }, []);
  const [activeSection, setActiveSection] =
    React.useState<PreviewSectionId>('project');

  const form = useAppForm({
    ...formOpts,
    defaultValues: initialValues,
    validators: {
      ...formOpts.validators,
      onSubmitAsync: async ({ value }) => {
        try {
          const activeDraftId = await prepareDraftForSubmitRef.current(value);
          await submitRequest(value, openSectionsRef.current, activeDraftId);
          if (activeDraftId) {
            clearLocalDraft(activeDraftId);
          }
          navigate('/requests/success');
          return void 0;
        } catch (error) {
          return errorToFormIssue(error);
        }
      },
    },
  });
  const { status: saveStatus, prepareDraftForSubmit } = useRequestDraftAutosave(
    form,
    {
      draftId,
      onDraftCreated: (createdDraftId) => {
        onDraftCreated(createdDraftId);
      },
    },
  );
  prepareDraftForSubmitRef.current = prepareDraftForSubmit;

  const openSectionsValue = React.useMemo(
    () => ({ openSections, setSectionOpen }),
    [openSections, setSectionOpen],
  );
  const activeSectionValue = React.useMemo(
    () => ({ activeSection, setActiveSection }),
    [activeSection],
  );

  const requestType = useStore(form.store, (state) => state.values.requestType);
  const [pendingType, setPendingType] = React.useState<RequestType | null>(
    null,
  );

  function applyRequestType(next: RequestType) {
    for (const key of SECTION_KEYS_BY_REQUEST_TYPE[otherRequestType(next)]) {
      form.setFieldValue(key, structuredClone(formDefaults[key]));
      setSectionOpen(key, false);
    }
    form.setFieldValue('requestType', next);
  }

  function handleRequestTypeChange(next: RequestType) {
    if (next === requestType) {
      return;
    }
    const outgoingHasSelections = SECTION_KEYS_BY_REQUEST_TYPE[
      requestType
    ].some((key) => openSectionsRef.current[key]);
    if (outgoingHasSelections) {
      setPendingType(next);
      return;
    }
    applyRequestType(next);
  }

  return (
    <OpenSectionsContext value={openSectionsValue}>
      <ActiveSectionContext value={activeSectionValue}>
        <div className="page-container">
          <form.AppForm>
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_10%_35%]">
              <form
                className="min-w-0"
                onSubmit={async (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  await form.handleSubmit();
                }}
              >
                {import.meta.env.DEV && (
                  <form.Subscribe
                    selector={(state) => state.errors}
                    children={(errors) => (
                      <p>{JSON.stringify(errors, null, 2)}</p>
                    )}
                  />
                )}
                <h1 className="mb-8 flex items-center gap-3 text-3xl font-bold">
                  {t('requests.newRequest')}
                  <SaveStatusHint status={saveStatus} />
                </h1>
                <RequestTypeField
                  value={requestType}
                  onChange={handleRequestTypeChange}
                />
                <ProjectSection form={form} />
                <MedicalStaffSection form={form} />
                <MedicalEquipmentSection form={form} />
                <BookingSection form={form} />
                <ContractTermsSection form={form} />
                <AdditionalDetailsSection form={form} />
                <form.ErrorMessage />
                <form.SubscribeButton label={t('requests.submitRequest')} />
              </form>
              <div></div>
              {/* Second in DOM but rendered left of the form: the grid is RTL */}
              <RequestPreviewPanel form={form} />
            </div>
          </form.AppForm>
        </div>
        <AlertDialog
          open={pendingType !== null}
          onOpenChange={(open) => {
            if (!open) {
              setPendingType(null);
            }
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {t('newRequest.changeRequestTypeTitle')}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {t('newRequest.changeRequestTypeDescription')}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('shared.common.cancel')}</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  if (pendingType) {
                    applyRequestType(pendingType);
                  }
                  setPendingType(null);
                }}
              >
                {t('newRequest.changeRequestTypeConfirm')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </ActiveSectionContext>
    </OpenSectionsContext>
  );
}
